/**
 * Estado da camada de qualidade do ar (Zustand). Registra o codec de URL `p`
 * (de "partículas": `a` já é das áreas dos focos) no import — `ui/modules.tsx`
 * importa este arquivo antes de `readUrlState()`.
 */
import { create } from 'zustand';
import { registerUrlCodec } from '../state/urlCodec';
import type { Cams, CamsSeries, Estilo, Pontos, Situacao } from './types';

export const DEFAULT_ESTILO: Estilo = {
  // O hexágono precisa de um pouco mais de raio que o círculo dos rios para
  // a forma se ler (medido no enquadramento inicial, sobre a imagem noturna).
  tamanho: 7,
  opacidade: 0.95,
  // Parado aparece por padrão, pequeno e vazado: a rede existe ali, e dizer
  // "sem dado" é informação — o sensor de Capixaba parado desde março conta.
  mostrarParados: true,
  // O modelo é um complemento interno da camada e nasce DESLIGADO: a camada é
  // de estações pontuais, medidas em solo. Quem quiser a estimativa regional
  // liga no painel; ela vem rotulada como estimativa no painel, na legenda e
  // na ficha.
  modelo: false,
  // Medido sobre o GeoColor de dia: com 50% a grade apagava as nuvens e a
  // fumaça por baixo; com 35% as faixas ainda se distinguem.
  opacidadeModelo: 0.35,
};

export const LIMITES = {
  tamanho: { min: 4, max: 12, step: 0.5 },
  opacidade: { min: 0.2, max: 1, step: 0.05 },
  opacidadeModelo: { min: 0.1, max: 0.9, step: 0.05 },
} as const;

/** A camada começa desligada, como as outras camadas extras. */
export const DEFAULT_VISIVEL = false;

const PONTOS_VAZIOS: Pontos = {
  gerado: null,
  sensores: [],
  t0: 0,
  passoMin: 5,
  slots: 0,
  municipios: new Map(),
  municipiosH0: 0,
  estacoes: [],
  h0: 0,
  horas: 48,
  fontes: {},
};

export interface ArState {
  visivel: boolean;
  estilo: Estilo;
  situacao: Situacao;
  pontos: Pontos;
  situacaoModelo: Situacao;
  cams: Cams | null;
  /** Séries completas do modelo: só descem na primeira ficha de célula aberta. */
  series: CamsSeries | null;
  situacaoSeries: Situacao;
  /** Célula do modelo com a ficha aberta (o mapa a contorna), ou `null`. */
  celulaAberta: number | null;

  setVisivel: (on: boolean) => void;
  setEstilo: (patch: Partial<Estilo>) => void;
}

export const useArStore = create<ArState>()((set) => ({
  visivel: DEFAULT_VISIVEL,
  estilo: DEFAULT_ESTILO,
  situacao: { estado: 'idle', erro: null },
  pontos: PONTOS_VAZIOS,
  situacaoModelo: { estado: 'idle', erro: null },
  cams: null,
  series: null,
  situacaoSeries: { estado: 'idle', erro: null },
  celulaAberta: null,

  setVisivel: (visivel) => set({ visivel }),
  setEstilo: (patch) => set((s) => ({ estilo: { ...s.estilo, ...patch } })),
}));

/**
 * `p=1` liga a camada; as letras seguintes guardam só o que difere do padrão:
 * `t` tamanho, `o` opacidade, `s` inverte "mostrar parados", `c` liga o
 * modelo CAMS, `g` opacidade do modelo (grade). Ex.: `p=1c` (com o modelo).
 * `m` (sem o modelo) vem dos links de quando ele nascia ligado: hoje é o padrão.
 */
export function encodeAr(s: Pick<ArState, 'visivel' | 'estilo'>): string | null {
  if (!s.visivel) return null;
  const e = s.estilo;
  let out = '1';
  if (e.tamanho !== DEFAULT_ESTILO.tamanho) out += `t${e.tamanho}`;
  if (e.opacidade !== DEFAULT_ESTILO.opacidade) out += `o${e.opacidade}`;
  if (e.mostrarParados !== DEFAULT_ESTILO.mostrarParados) out += 's';
  if (e.modelo) out += 'c';
  if (e.opacidadeModelo !== DEFAULT_ESTILO.opacidadeModelo) out += `g${e.opacidadeModelo}`;
  return out;
}

export function decodeAr(value: string): Partial<Pick<ArState, 'visivel' | 'estilo'>> | null {
  if (!value || value[0] !== '1') return null;
  const estilo: Estilo = { ...DEFAULT_ESTILO };
  const t = /t(\d+(?:\.\d+)?)/.exec(value);
  if (t) estilo.tamanho = limitar(Number(t[1]), LIMITES.tamanho.min, LIMITES.tamanho.max, DEFAULT_ESTILO.tamanho);
  const o = /o(\d*\.?\d+)/.exec(value);
  if (o) estilo.opacidade = limitar(Number(o[1]), LIMITES.opacidade.min, LIMITES.opacidade.max, DEFAULT_ESTILO.opacidade);
  const g = /g(\d*\.?\d+)/.exec(value);
  if (g) {
    estilo.opacidadeModelo = limitar(
      Number(g[1]),
      LIMITES.opacidadeModelo.min,
      LIMITES.opacidadeModelo.max,
      DEFAULT_ESTILO.opacidadeModelo,
    );
  }
  if (value.includes('s')) estilo.mostrarParados = !DEFAULT_ESTILO.mostrarParados;
  estilo.modelo = value.includes('c');
  return { visivel: true, estilo };
}

function limitar(n: number, min: number, max: number, padrao: number): number {
  return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : padrao;
}

registerUrlCodec({
  key: 'p',
  encode: () => encodeAr(useArStore.getState()),
  decode: (value) => {
    const s = decodeAr(value);
    if (s) useArStore.setState(s);
  },
  subscribe: (onChange) =>
    useArStore.subscribe((a, b) => {
      if (a.visivel !== b.visivel || a.estilo !== b.estilo) onChange();
    }),
});
